import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { useTheme } from "../contexts/ThemeContext";
import { createBackgroundGradient } from "../constants/theme";
import { useLiveShowQuery } from "../hooks/queries/useLiveShowQuery";
import { ApiService } from "../services/apiService";
import { renderSubTitleIfValid } from "../utils/subTitleUtils";
import { LoadingSpinner } from "./ui/LoadingStates";
import StoreErrorBoundary from "./common/StoreErrorBoundary";
import { debugError, debugWarn } from "../utils/debugLog";
import { Show } from "../types/Show";
import OptimizedContentImage from "./common/OptimizedContentImage";
import { useListeningTelemetry } from "../hooks/useListeningTelemetry";
import { DEFAULT_SHOW_FALLBACK_IMAGE } from "../constants/images";

const DEFAULT_IMAGE_URL = DEFAULT_SHOW_FALLBACK_IMAGE;

type DisplayImages = Record<string, string | undefined>;

interface DisplayInfo {
  show: string;
  presenter?: { name: string };
  images?: DisplayImages;
  sub_title?: string;
}

const mapShowToDisplayInfo = (show?: Show | null): DisplayInfo | null => {
  if (!show) {
    return null;
  }

  return {
    show: show.show,
    presenter: show.presenter,
    images: { ...show.images },
    sub_title: show.sub_title,
  };
};

const buildFallbackDisplayInfo = (
  title: string,
  presenterName: string,
  artwork?: string
): DisplayInfo => ({
  show: title,
  presenter: { name: presenterName },
  images: { "300x300": artwork || DEFAULT_IMAGE_URL },
});

const sanitizeDisplayText = (value?: string | null): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const normalized = trimmed.toLowerCase();
  if (["null", "undefined", "n/a", "na", "none"].includes(normalized)) {
    return undefined;
  }

  return trimmed;
};

const sanitizeImageCandidate = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const normalized = trimmed.toLowerCase();
  if (normalized === 'null' || normalized === 'undefined') {
    return undefined;
  }

  if (trimmed.endsWith('/')) {
    return undefined;
  }

  return trimmed;
};

const ensureDisplayInfo = (
  info: DisplayInfo | null,
  fallbackTitle: string,
  fallbackPresenter: string,
  fallbackImage: string = DEFAULT_IMAGE_URL
): DisplayInfo => {
  const safeShow =
    sanitizeDisplayText(info?.show) ??
    sanitizeDisplayText(info?.presenter?.name) ??
    sanitizeDisplayText(info?.sub_title) ??
    sanitizeDisplayText(fallbackTitle) ??
    fallbackTitle;

  const safePresenter =
    sanitizeDisplayText(info?.presenter?.name) ??
    sanitizeDisplayText(info?.show) ??
    sanitizeDisplayText(fallbackPresenter) ??
    fallbackPresenter;

  const cleanedImages: DisplayImages = {};
  if (info?.images) {
    for (const [key, value] of Object.entries(info.images)) {
      const candidate = sanitizeImageCandidate(value);
      if (candidate) {
        cleanedImages[key] = candidate;
      }
    }
  }

  if (Object.keys(cleanedImages).length === 0) {
    cleanedImages["300x300"] = fallbackImage;
  }

  return {
    show: safeShow,
    presenter: { name: safePresenter },
    images: cleanedImages,
    sub_title: sanitizeDisplayText(info?.sub_title),
  };
};

const selectFirstAvailableImage = (
  images: DisplayImages | undefined,
  priorities: string[]
): string | undefined => {
  if (!images) {
    return undefined;
  }

  for (const key of priorities) {
    const candidate = sanitizeImageCandidate(images[key]);
    if (candidate) {
      return candidate;
    }
  }

  return undefined;
};

const areDisplayInfoEqual = (a: DisplayInfo, b: DisplayInfo): boolean => {
  if (a === b) {
    return true;
  }

  if (a.show !== b.show) {
    return false;
  }

  if ((a.presenter?.name ?? "") !== (b.presenter?.name ?? "")) {
    return false;
  }

  if ((a.sub_title ?? "") !== (b.sub_title ?? "")) {
    return false;
  }

  const imageKeysA = Object.keys(a.images ?? {});
  const imageKeysB = Object.keys(b.images ?? {});

  if (imageKeysA.length !== imageKeysB.length) {
    return false;
  }

  for (const key of imageKeysA) {
    if ((a.images ?? {})[key] !== (b.images ?? {})[key]) {
      return false;
    }
  }

  return true;
};

interface SeekIconProps {
  direction: "back" | "forward";
  className?: string;
}

const SeekIcon30: React.FC<SeekIconProps> = ({ direction, className = "" }) => {
  const combinedClassName = ["transition-transform", className].filter(Boolean).join(" ");

  const arrowTransform =
    direction === "forward" ? "scale(-1,1) translate(-32,0)" : undefined;

  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={combinedClassName}
    >
      <path
        fill="currentColor"
        d="M4,18A12,12,0,1,0,16,6H12V1L6,7l6,6V8h4A10,10,0,1,1,6,18Z"
        transform={arrowTransform}
      />
      <path
        fill="currentColor"
        d="M19.64,22.13a2.81,2.81,0,0,1-1.28-.27,2.36,2.36,0,0,1-.89-.77A3.39,3.39,0,0,1,17,19.84a7.12,7.12,0,0,1-.17-1.68A7.24,7.24,0,0,1,17,16.48a3.46,3.46,0,0,1,.52-1.25,2.36,2.36,0,0,1,.89-.77,2.81,2.81,0,0,1,1.28-.27,2.44,2.44,0,0,1,2.16,1,5.31,5.31,0,0,1,.7,2.93,5.31,5.31,0,0,1-.7,2.93A2.44,2.44,0,0,1,19.64,22.13Zm0-1.22a1,1,0,0,0,1-.55,3.24,3.24,0,0,0,.3-1.51V17.47a3.17,3.17,0,0,0-.3-1.5,1.22,1.22,0,0,0-2.05,0,3.18,3.18,0,0,0-.29,1.5v1.38a3.25,3.25,0,0,0,.29,1.51A1,1,0,0,0,19.64,20.91Z"
      />
      <path
        fill="currentColor"
        d="M12.62,17.42a1.46,1.46,0,0,0,1-.27.84.84,0,0,0,.31-.68v-.08a.94.94,0,0,0-.3-.74,1.2,1.2,0,0,0-.83-.27,1.65,1.65,0,0,0-.89.24,2.1,2.1,0,0,0-.68.68l-.93-.83a5.37,5.37,0,0,1,.44-.51,2.7,2.7,0,0,1,.54-.4,2.55,2.55,0,0,1,.7-.27,3.25,3.25,0,0,1,.87-.1,3.94,3.94,0,0,1,1.06.14,2.33,2.33,0,0,1,.82.4,1.91,1.91,0,0,1,.54.63,1.87,1.87,0,0,1,.18.83,2,2,0,0,1-.11.67,1.82,1.82,0,0,1-.32.52,1.79,1.79,0,0,1-.47.36,2.27,2.27,0,0,1-.57.2V18a2.34,2.34,0,0,1,.63.21,1.7,1.7,0,0,1,.51.38,1.89,1.89,0,0,1,.34.55,2.07,2.07,0,0,1,.12.73,2,2,0,0,1-.2.92,2,2,0,0,1-.58.72,2.66,2.66,0,0,1-.89.45,3.76,3.76,0,0,1-1.15.16,4.1,4.1,0,0,1-1-.11A3.1,3.1,0,0,1,11,21.7a2.76,2.76,0,0,1-.56-.45A4.22,4.22,0,0,1,10,20.7l1.07-.81a3.07,3.07,0,0,0,.28.42,1.94,1.94,0,0,0,.36.34,1.57,1.57,0,0,0,.45.22,2,2,0,0,0,.57.07,1.45,1.45,0,0,0,1-.3,1.12,1.12,0,0,0,.34-.85v-.08a1,1,0,0,0-.37-.8,1.78,1.78,0,0,0-1.06-.28h-.76V17.42Z"
      />
    </svg>
  );
};

interface SeekControlButtonProps {
  direction: SeekIconProps["direction"];
  onClick: () => void;
  disabled?: boolean;
  size?: "default" | "compact";
  ariaLabel: string;
  title: string;
}

const SeekControlButton: React.FC<SeekControlButtonProps> = ({
  direction,
  onClick,
  disabled = false,
  size = "default",
  ariaLabel,
  title,
}) => {
  const dimensionClass = size === "compact" ? "w-10 h-10" : "w-12 h-12";
  const iconDimension = size === "compact" ? "w-5 h-5" : "w-6 h-6";
  const interactiveClasses = disabled ? "" : "hover:scale-[1.05] active:scale-[0.97]";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "group relative inline-flex items-center justify-center overflow-hidden rounded-full",
        "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 text-slate-200",
        "shadow-[0_12px_28px_rgba(15,23,42,0.55)] transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/70",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        dimensionClass,
        interactiveClasses,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={ariaLabel}
      title={title}
    >
      <span
        className="absolute inset-0 rounded-full border border-white/10 transition-colors duration-200 ease-out group-hover:border-red-400/50 group-focus-visible:border-red-400/70"
        aria-hidden="true"
      />
      <span
        className="absolute inset-[1px] rounded-full bg-slate-900/70 group-hover:bg-slate-900/80 group-active:bg-slate-900/90 transition-colors duration-200 ease-out"
        aria-hidden="true"
      />
      <span className="relative flex items-center justify-center text-current drop-shadow-[0_2px_6px_rgba(15,23,42,0.55)]">
        <SeekIcon30 direction={direction} className={iconDimension} />
      </span>
    </button>
  );
};

const GlobalAudioPlayerContent: React.FC = () => {
  // Use theme with fallback to dark mode for backward compatibility
  let mode: 'light' | 'dark' = 'dark';
  try {
    const themeContext = useTheme();
    mode = themeContext.mode;
  } catch (error) {
    // Fallback to dark mode if ThemeProvider is not available
    debugWarn('ThemeProvider not found, using dark mode as fallback');
  }
  
  const {
    currentTrack,
    isPlaying,
    volume,
    isPlayerVisible,
    currentTime,
    duration,
    pause,
    resume,
    setVolume,
    seekTo,
    updateCurrentTime,
    updateDuration,
  } = useAudioPlayer();

  // Use dedicated live show API instead of schedule-derived data
  const { data: liveShow } = useLiveShowQuery();
  const audioRef = useRef<HTMLAudioElement>(null);
  const currentTimeRef = useRef(currentTime);
  const lastTrackIdRef = useRef<string | null>(null);
  const suppressNextPauseRef = useRef(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Determine what to display - current track or live show
  const liveDisplayInfo =
    mapShowToDisplayInfo(liveShow) ??
    buildFallbackDisplayInfo("Eruption Radio", "Live Stream");

  const recordedDisplayInfo =
    mapShowToDisplayInfo(currentTrack?.show ?? null) ??
    buildFallbackDisplayInfo(
      currentTrack?.title || "Eruption Radio",
      currentTrack?.artist || "Live Stream",
      currentTrack?.artwork
    );

  const isRecordedTrack = currentTrack?.isLive === false;

  const initialLiveDisplayInfo = useMemo(
    () => ensureDisplayInfo(liveDisplayInfo, "Eruption Radio", "Live Stream"),
    [liveDisplayInfo]
  );

  const [stableLiveDisplayInfo, setStableLiveDisplayInfo] = useState<DisplayInfo>(
    initialLiveDisplayInfo
  );
  const lastLiveDisplayRef = useRef<DisplayInfo>(initialLiveDisplayInfo);

  const sanitizedLiveInfo = useMemo(() => {
    const fallbackImage =
      selectFirstAvailableImage(lastLiveDisplayRef.current.images, [
        "600x600",
        "500x500",
        "400x400",
        "300x300"
      ]) ?? DEFAULT_IMAGE_URL;

    return ensureDisplayInfo(
      liveDisplayInfo,
      lastLiveDisplayRef.current.show,
      lastLiveDisplayRef.current.presenter?.name || "Live Stream",
      fallbackImage
    );
  }, [liveDisplayInfo]);

  useEffect(() => {
    if (!areDisplayInfoEqual(sanitizedLiveInfo, lastLiveDisplayRef.current)) {
      lastLiveDisplayRef.current = sanitizedLiveInfo;
      setStableLiveDisplayInfo(sanitizedLiveInfo);
    }
  }, [sanitizedLiveInfo]);

  const safeRecordedDisplayInfo = useMemo(() => {
    const fallbackTitle =
      sanitizeDisplayText(currentTrack?.title) ??
      sanitizeDisplayText(currentTrack?.show?.show) ??
      "Eruption Radio";
    const fallbackPresenter =
      sanitizeDisplayText(currentTrack?.show?.presenter?.name) ??
      sanitizeDisplayText(currentTrack?.artist) ??
      "Eruption Radio";

    return ensureDisplayInfo(
      recordedDisplayInfo,
      fallbackTitle,
      fallbackPresenter,
      currentTrack?.artwork || DEFAULT_IMAGE_URL
    );
  }, [currentTrack?.artwork, currentTrack?.artist, currentTrack?.show, currentTrack?.title, recordedDisplayInfo]);

  const displayInfo = isRecordedTrack ? safeRecordedDisplayInfo : stableLiveDisplayInfo;

  useListeningTelemetry({
    track: currentTrack,
    isPlaying,
    currentTime
  });

  // Helper function to get image URL safely
  const getImageUrl = (size: "small" | "large" = "small") => {
    const priorities =
      size === "large"
        ? ["600x600", "500x500", "400x400", "300x300"]
        : ["300x300", "150x150"];

    return selectFirstAvailableImage(displayInfo.images, priorities) ?? DEFAULT_IMAGE_URL;
  };

  // Format time for display (MM:SS or HH:MM:SS)
  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return "0:00";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    }
  };

  // Separate function for starting playback
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  const startPlayback = useCallback(async () => {
    if (!audioRef.current || !currentTrack) return;

    setIsLoading(true);
    setStreamError(false);

    // Set the appropriate URL based on track type
    audioRef.current.src = currentTrack.url;

    // For recorded shows, set the current time if resuming
    if (!currentTrack.isLive && currentTimeRef.current > 0) {
      audioRef.current.currentTime = currentTimeRef.current;
    }

    await audioRef.current.play();
    resume();
    setIsLoading(false);
  }, [currentTrack, resume]);

  const tryBackupStream = useCallback(async () => {
    if (!audioRef.current || !currentTrack?.isLive) return;

    try {
      const backupUrl =
        process.env.REACT_APP_STREAM_BACKUP ||
        "https://cosmo.shoutca.st/proxy/eruptionradio/stream";
      audioRef.current.src = backupUrl;
      await audioRef.current.play();
      setStreamError(false);
      resume();
    } catch (error) {
      debugError("Backup stream failed:", error);
      setStreamError(true);
    }
  }, [currentTrack, resume]);

  // Handle play/pause
  const handlePlayPause = useCallback(async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        pause();
      } else {
        await startPlayback();
      }
    } catch (error) {
      debugError("Playback error:", error);
      setStreamError(true);
      tryBackupStream();
    }
  }, [isPlaying, pause, startPlayback, tryBackupStream]);

  // Handle scrubbing for recorded shows
  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current || !currentTrack || currentTrack.isLive) return;

    const newTime = parseFloat(event.target.value);
    audioRef.current.currentTime = newTime;
    seekTo(newTime);
    updateCurrentTime(newTime);
  };

  const seekByOffset = useCallback(
    (offsetSeconds: number) => {
      if (!audioRef.current || !currentTrack || currentTrack.isLive) {
        return;
      }

      const audio = audioRef.current;
      const currentPosition = Number.isFinite(audio.currentTime) ? audio.currentTime : currentTime;
      const audioDuration = Number.isFinite(audio.duration)
        ? audio.duration
        : currentTrack.duration ?? duration ?? 0;

      const cappedDuration = audioDuration > 0 ? audioDuration : Math.max(currentPosition, 0);
      const targetTime = Math.min(
        Math.max(0, currentPosition + offsetSeconds),
        cappedDuration > 0 ? cappedDuration : Math.max(0, currentPosition + offsetSeconds)
      );

      audio.currentTime = targetTime;
      seekTo(targetTime);
      updateCurrentTime(targetTime);
    },
    [currentTrack, currentTime, duration, seekTo, updateCurrentTime]
  );

  const handleSeekBackwardThirty = useCallback(() => {
    seekByOffset(-30);
  }, [seekByOffset]);

  const handleSeekForwardThirty = useCallback(() => {
    seekByOffset(30);
  }, [seekByOffset]);

  // Handle time updates
  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current || !currentTrack || currentTrack.isLive) {
      return;
    }

    updateCurrentTime(audioRef.current.currentTime);
  }, [currentTrack, updateCurrentTime]);

  // Handle duration loaded
  const handleLoadedMetadata = useCallback(() => {
    if (!audioRef.current || !currentTrack || currentTrack.isLive) {
      return;
    }

    updateDuration(audioRef.current.duration);
  }, [currentTrack, updateDuration]);

  // Effect to handle track changes - prepare for new track but don't load until play
  useEffect(() => {
    const audio = audioRef.current;
    const trackId = currentTrack?.id ?? null;

    if (!audio) {
      lastTrackIdRef.current = trackId;
      return;
    }

    if (!currentTrack) {
      // Clear playback state when the track is removed (e.g. stop action)
      lastTrackIdRef.current = null;
      suppressNextPauseRef.current = true;
      audio.pause();
      audio.currentTime = 0;
      setIsLoading(false);
      setStreamError(false);
      return;
    }

    if (lastTrackIdRef.current === trackId) {
      return;
    }

    lastTrackIdRef.current = trackId;
    suppressNextPauseRef.current = true;

    audio.pause();
    audio.currentTime = 0;

    setIsLoading(false);
    setStreamError(false);

    if (isPlaying) {
      startPlayback().catch((error) => {
        debugError("Auto-play failed:", error);
        setStreamError(true);
        if (currentTrack.isLive) {
          tryBackupStream();
        }
      });
    }
  }, [currentTrack, isPlaying, startPlayback, tryBackupStream]);

  // Effect to handle play/pause state changes
  useEffect(() => {
    if (audioRef.current && currentTrack) {
      if (isPlaying && audioRef.current.paused) {
        startPlayback().catch((error) => {
          debugError("Auto-play failed:", error);
          setStreamError(true);
          if (currentTrack.isLive) {
            tryBackupStream();
          }
        });
      } else if (!isPlaying && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    }
  }, [currentTrack, isPlaying, startPlayback, tryBackupStream]);

  // Handle volume changes
  const handleVolumeChange = (newVolume: number) => {
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  };

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handlePlay = () => resume();
    const handlePause = () => {
      if (suppressNextPauseRef.current) {
        suppressNextPauseRef.current = false;
        return;
      }
      pause();
    };
    const handleError = () => {
      setStreamError(true);
      setIsLoading(false);
      if (currentTrack?.isLive) {
        tryBackupStream();
      }
    };

    audio.addEventListener("loadstart", handleLoadStart);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("loadstart", handleLoadStart);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [pause, resume, currentTrack, handleTimeUpdate, handleLoadedMetadata, tryBackupStream]);

  if (!isPlayerVisible) return null;

  return (
    <aside aria-label="Global audio player" className="print:hidden">
      {/* Hidden audio element */}
      <audio ref={audioRef} preload="none" crossOrigin="anonymous" />

      {/* Prominent Audio Player */}
      <div
        className={`fixed left-0 right-0 z-50 border-t border-red-500 shadow-2xl transition-all duration-300 ${
          isMinimized
            ? "h-16"
            : currentTrack?.isLive
            ? "h-20 sm:h-28"
            : "h-24 sm:h-32"
        } bottom-0 sm:pt-1`}
        style={{
          background: createBackgroundGradient(mode)
        }}
      >
        {/* Mobile Player */}
        <div className="block sm:hidden border-b border-gray-700 sm:border-b-0">
          <div
            className={`flex flex-col ${
              currentTrack?.isLive ? "h-20" : "h-24"
            } pb-2`}
          >
            {/* Main content row */}
            <div className="flex items-center justify-between p-3 flex-1">
              {/* Show Info */}
              <div
                className="flex items-center space-x-3 flex-1 min-w-0"
                aria-live="polite"
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                  <OptimizedContentImage
                    src={getImageUrl("small")}
                    alt={displayInfo.show}
                    className="w-full h-full"
                    imageClassName="w-full h-full object-cover"
                    fallbackSrc={DEFAULT_IMAGE_URL}
                    sizes="40px"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-bold truncate">
                    {displayInfo.presenter?.name || "Eruption Radio"}
                  </p>
                  {/* Sub-title (replaces old show title) - with graceful null/empty handling */}
                  {renderSubTitleIfValid(displayInfo.sub_title, (validSubTitle) => (
                    <p className="text-white text-xs truncate font-medium">
                      {validSubTitle}
                      {currentTrack?.isLive && liveShow && (
                        <span className="ml-2 inline-flex items-center">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse mr-1"></span>
                          LIVE
                        </span>
                      )}
                    </p>
                  ))}
                  {/* Fallback for when no sub_title is available */}
                  {!displayInfo.sub_title && (
                    <p className="text-gray-400 text-xs truncate">
                      {currentTrack?.isLive
                        ? "Live Stream"
                        : currentTrack?.title || "Live Stream"}
                      {currentTrack?.isLive && liveShow && (
                        <span className="ml-2 inline-flex items-center">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse mr-1"></span>
                          LIVE
                        </span>
                      )}
                    </p>
                  )}
                  {/* Duration for recorded shows */}
                  {!currentTrack?.isLive && currentTrack?.show?.duration && (
                    <p className="text-gray-500 text-xs">
                      {ApiService.formatDuration(currentTrack.show.duration)}
                    </p>
                  )}
                  {streamError && (
                    <p className="text-yellow-400 text-xs">Trying backup...</p>
                  )}
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center space-x-3">
                {isRecordedTrack && (
                  <SeekControlButton
                    direction="back"
                    onClick={handleSeekBackwardThirty}
                    disabled={isLoading}
                    size="compact"
                    ariaLabel="Rewind 30 seconds"
                    title="Rewind 30 seconds"
                  />
                )}
                <button
                  type="button"
                  onClick={handlePlayPause}
                  disabled={isLoading}
                  className="w-12 h-12 bg-red-500 hover:bg-red-600 disabled:bg-gray-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
                  aria-label={
                    isLoading
                      ? "Audio loading"
                      : isPlaying
                      ? "Pause audio"
                      : "Play audio"
                  }
                >
                  {isLoading ? (
                    <LoadingSpinner 
                      size="small" 
                      className="text-white" 
                      includeRole={false}
                    />
                  ) : isPlaying ? (
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg
                      className="w-6 h-6 text-white ml-0.5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
                {isRecordedTrack && (
                  <SeekControlButton
                    direction="forward"
                    onClick={handleSeekForwardThirty}
                    disabled={isLoading}
                    size="compact"
                    ariaLabel="Skip forward 30 seconds"
                    title="Skip forward 30 seconds"
                  />
                )}
              </div>
            </div>

            {/* Progress bar for recorded shows (mobile) */}
            {!currentTrack?.isLive && duration > 0 && (
              <div className="px-3 pb-3">
                <div className="flex items-center space-x-2 text-xs text-gray-400">
                  <span className="w-10 text-right">
                    {formatTime(currentTime)}
                  </span>
                  <input
                    type="range"
                    min="0"
                    max={duration}
                    value={currentTime}
                    onChange={handleSeek}
                    className="flex-1 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    aria-label="Seek position"
                    aria-valuetext={formatTime(currentTime)}
                  />
                  <span className="text-gray-500 text-xs ml-2">
                    {currentTrack?.show?.duration
                      ? ApiService.formatDuration(currentTrack.show.duration)
                      : formatTime(duration)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Player */}
        <div className="hidden sm:block">
          <div className={`${currentTrack?.isLive ? "" : "flex flex-col"}`}>
            <div
              className={`flex items-center justify-between p-4 ${
                isMinimized
                  ? "h-16"
                  : currentTrack?.isLive
                  ? "h-24 sm:h-28"
                  : "h-20"
              }`}
            >
              {/* Show Info Section */}
              <div className="flex items-center space-x-4 flex-1 min-w-0 max-w-md">
                <div
                  className={`${
                    isMinimized ? "w-12 h-12" : "w-16 h-16"
                  } rounded-xl overflow-hidden bg-gray-800 flex-shrink-0 shadow-lg`}
                >
                  <OptimizedContentImage
                    src={getImageUrl("large")}
                    alt={displayInfo.show}
                    className="w-full h-full"
                    imageClassName="w-full h-full object-cover"
                    fallbackSrc={DEFAULT_IMAGE_URL}
                    sizes={isMinimized ? "64px" : "96px"}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-white font-bold truncate ${
                      isMinimized ? "text-base" : "text-lg"
                    }`}
                  >
                    {displayInfo.presenter?.name || "Eruption Radio"}
                  </p>
                  {/* Sub-title (replaces old show title) - with graceful null/empty handling */}
                  {renderSubTitleIfValid(displayInfo.sub_title, (validSubTitle) => (
                    <p
                      className={`text-white truncate font-medium ${
                        isMinimized ? "text-sm" : "text-base"
                      }`}
                    >
                      {validSubTitle}
                      {currentTrack?.isLive && liveShow && (
                        <span className="ml-2 inline-flex items-center">
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-1"></span>
                          LIVE
                        </span>
                      )}
                    </p>
                  ))}
                  {/* Fallback for when no sub_title is available */}
                  {!displayInfo.sub_title && (
                    <p
                      className={`text-gray-400 truncate ${
                        isMinimized ? "text-sm" : "text-base"
                      }`}
                    >
                      {currentTrack?.isLive
                        ? "Live Stream"
                        : currentTrack?.title || "Live Stream"}
                      {currentTrack?.isLive && liveShow && (
                        <span className="ml-2 inline-flex items-center">
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-1"></span>
                          LIVE
                        </span>
                      )}
                    </p>
                  )}
                  {streamError && (
                    <p className="text-yellow-400 text-sm">
                      Trying backup stream...
                    </p>
                  )}
                </div>
              </div>

              {/* Main Controls */}
              <div className="flex items-center space-x-6 flex-1 justify-center max-w-md">
                {isRecordedTrack && (
                  <SeekControlButton
                    direction="back"
                    onClick={handleSeekBackwardThirty}
                    disabled={isLoading}
                    size={isMinimized ? "compact" : "default"}
                    ariaLabel="Rewind 30 seconds"
                    title="Rewind 30 seconds"
                  />
                )}
                <button
                  type="button"
                  onClick={handlePlayPause}
                  disabled={isLoading}
                  className={`${
                    isMinimized ? "w-12 h-12" : "w-16 h-16"
                  } bg-red-500 hover:bg-red-600 disabled:bg-gray-600 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-xl`}
                  aria-label={
                    isLoading
                      ? "Audio loading"
                      : isPlaying
                      ? "Pause audio"
                      : "Play audio"
                  }
                >
                  {isLoading ? (
                    <LoadingSpinner 
                      size={isMinimized ? "small" : "medium"} 
                      className="text-white" 
                      includeRole={false}
                    />
                  ) : isPlaying ? (
                    <svg
                      className={`${
                        isMinimized ? "w-6 h-6" : "w-8 h-8"
                      } text-white`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg
                      className={`${
                        isMinimized ? "w-6 h-6" : "w-8 h-8"
                      } text-white ml-0.5`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>

                {isRecordedTrack && (
                  <SeekControlButton
                    direction="forward"
                    onClick={handleSeekForwardThirty}
                    disabled={isLoading}
                    size={isMinimized ? "compact" : "default"}
                    ariaLabel="Skip forward 30 seconds"
                    title="Skip forward 30 seconds"
                  />
                )}

                {!isMinimized && currentTrack?.isLive && (
                  <div className="flex items-center space-x-3">
                    <svg
                      className="w-4 h-4 text-slate-200"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={(e) =>
                        handleVolumeChange(parseFloat(e.target.value))
                      }
                      className="w-20 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                      aria-label="Volume"
                      aria-valuetext={`${Math.round(volume * 100)} percent`}
                    />
                  </div>
                )}
              </div>

              {/* Additional Controls */}
              <div className="flex items-center space-x-4 flex-1 justify-end max-w-md">
                <button
                  type="button"
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="inline-flex items-center justify-center text-slate-200 hover:text-white transition-colors p-3 min-w-[44px] min-h-[44px] rounded-full"
                  title={isMinimized ? "Expand" : "Minimize"}
                  aria-label={isMinimized ? "Expand audio player" : "Minimize audio player"}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={isMinimized ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Progress bar for recorded shows (desktop) */}
            {!currentTrack?.isLive && duration > 0 && !isMinimized && (
              <div className="px-4 pb-3">
                <div className="flex items-center space-x-4 text-sm text-slate-200">
                  <span className="w-12 text-right">
                    {formatTime(currentTime)}
                  </span>
                  <input
                    type="range"
                    min="0"
                    max={duration}
                    value={currentTime}
                    onChange={handleSeek}
                    className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    aria-label="Seek position"
                    aria-valuetext={formatTime(currentTime)}
                  />
                  <span className="text-gray-400 text-sm font-medium ml-4">
                    {currentTrack?.show?.duration
                      ? ApiService.formatDuration(currentTrack.show.duration)
                      : formatTime(duration)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Live indicator bar */}
        {currentTrack?.isLive && liveShow && (
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-red-500 via-red-400 to-red-500 animate-pulse"></div>
        )}
      </div>

      {/* Custom slider styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .slider::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #ef4444;
            cursor: pointer;
            border: 2px solid #fff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          
          .slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #ef4444;
            cursor: pointer;
            border: 2px solid #fff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
        `,
        }}
      />
    </aside>
  );
};

/**
 * GlobalAudioPlayer with error boundary protection
 */
export const GlobalAudioPlayer: React.FC = () => {
  return (
    <StoreErrorBoundary>
      <GlobalAudioPlayerContent />
    </StoreErrorBoundary>
  );
};
